import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChatRoom,
  fetchChatRooms,
  fetchItemShopWallet,
  fetchProceedingChatRooms,
  navigate,
  showOneliveAlert,
  type ItemShopWallet,
} from './api';
import { canEnterChatRoom, splitChatListRooms } from './chatListShared';
import { handleLoginRequired } from './liveEntryGuard';
import { useLiveUiMessages } from './useLiveUiMessages';
import { useOneliveSession } from './useOneliveSession';

const CHAT_LIST_LIMIT = 50;

export function useChatListPage() {
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [proceedingRooms, setProceedingRooms] = useState<ChatRoom[]>([]);
  const [wallet, setWallet] = useState<ItemShopWallet | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTitle, setSearchTitle] = useState('');
  const [searchLevel, setSearchLevel] = useState('');
  const [appliedTitle, setAppliedTitle] = useState('');
  const [appliedLevel, setAppliedLevel] = useState('');
  const { isAuthed, authReady } = useOneliveSession();
  const liveUiMessages = useLiveUiMessages();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, proceeding, walletData] = await Promise.all([
        fetchChatRooms(CHAT_LIST_LIMIT, appliedTitle || undefined, appliedLevel || undefined),
        fetchProceedingChatRooms(CHAT_LIST_LIMIT),
        isAuthed ? fetchItemShopWallet() : Promise.resolve(null),
      ]);
      const { proceedingRooms, rooms: listRooms } = splitChatListRooms(list, proceeding);
      setProceedingRooms(proceedingRooms);
      setRooms(listRooms);
      setWallet(walletData);
    } finally {
      setLoading(false);
    }
  }, [appliedTitle, appliedLevel, isAuthed]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalMembers = useMemo(
    () => proceedingRooms.reduce((sum, room) => sum + (room.current_occupancy ?? 0), 0),
    [proceedingRooms],
  );

  const onResetSearch = () => {
    setSearchTitle('');
    setSearchLevel('');
    setAppliedTitle('');
    setAppliedLevel('');
    setSearchOpen(false);
  };

  const onEnterRoom = (room: ChatRoom) => {
    if (!authReady) return;
    if (!isAuthed) {
      handleLoginRequired(liveUiMessages, '/chat');
      return;
    }

    if (!canEnterChatRoom(room)) {
      showOneliveAlert(room.status === 'closed' ? '채팅방이 종료되였습니다.' : '채팅방이 만원입니다.');
      return;
    }

    navigate(`/chat/room/${room.id}`);
  };

  const onSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    setAppliedTitle(searchTitle.trim());
    setAppliedLevel(searchLevel);
    setSearchOpen(false);
  };

  return {
    rooms,
    proceedingRooms,
    wallet,
    loading,
    searchOpen,
    setSearchOpen,
    searchTitle,
    setSearchTitle,
    searchLevel,
    setSearchLevel,
    isAuthed,
    totalMembers,
    load,
    onEnterRoom,
    onSearchSubmit,
    onResetSearch,
  };
}

export type ChatListPageState = ReturnType<typeof useChatListPage>;
